<template>
  <Modal
    :show="show"
    width="366"
    cancel-text=""
    action-text=""
    :disabled="disabled"
    @close="$emit('close')"
  >
    <template #title>
      <span class="text-h2">{{ title }}</span>
    </template>
    <template #content>
      <template v-if="result">
        <p
          v-for="line in resultLines"
          :key="line"
          class="text-body-1 text-textDark mb-0"
        >
          {{ line }}
        </p>
      </template>
      <p
        v-else
        class="text-body-1 text-textDarkest mb-0"
      >
        {{ bodyLine }}
      </p>
    </template>
    <template
      v-if="!result"
      #actions
    >
      <DpplNewButton
        class="bulk-modal-action text-button-1"
        :variant="openedAsUnarchive ? 'flat' : 'outlined'"
        :color="openedAsUnarchive ? 'primary' : 'error'"
        :disabled="disabled"
        :loading="loading"
        @click="$emit('confirm')"
      >
        {{ openedAsUnarchive ? 'Unarchive' : 'Archive' }}
      </DpplNewButton>
    </template>
  </Modal>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import Modal from '@/components/shared/Modal.vue';
import DpplNewButton from '@/components/datapeople/DpplNewButton.vue';
import { formatBulkArchiveResultLines } from '@/components/jobArchitecture/helpers';

const props = defineProps({
  show: { type: Boolean, default: false },
  count: { type: Number, default: 0 },
  isUnarchive: { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },
  loading: { type: Boolean, default: false },
  // Set after the action ran with some drafts skipped: { successCount, failedCount }.
  result: { type: Object, default: null },
});

defineEmits(['close', 'confirm']);

// Keep the mode from when the window opened, so it does not change while it closes.
const openedAsUnarchive = ref(props.isUnarchive);

watch(() => props.show, (isShown) => {
  if (isShown) openedAsUnarchive.value = props.isUnarchive;
}, { immediate: true });

const action = computed(() => (openedAsUnarchive.value ? 'Unarchive' : 'Archive'));

const title = computed(() => {
  if (props.result) return `${action.value} Job Profiles`;

  return props.count === 1 ? `${action.value} 1 Profile` : `${action.value} ${props.count} Profiles`;
});

const resultLines = computed(() => (
  props.result ? formatBulkArchiveResultLines(props.result, { isUnarchive: openedAsUnarchive.value }) : []
));

const bodyLine = computed(() => {
  const noun = props.count === 1 ? '1 job profile' : `${props.count} job profiles`;

  return openedAsUnarchive.value
    ? `This will move ${noun} back to your active list.`
    : `This will move ${noun} out of your active list. You can unarchive them any time by setting Status to Inactive.`;
});
</script>

<style scoped lang="less">
.bulk-modal-action {
  margin-top: 34px;
  width: 150px;
  height: 40px !important;
  min-height: 40px !important;
}
</style>
